import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Fairview = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.fairview-vancouver:About Fairview Paragraph">
          Fairview is a vibrant neighbourhood located on Vancouver’s west side
          in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          . Bordered by Burrard Street to the west, Cambie Street to the east,
          16th Avenue to the south, and False Creek to the north, Fairview
          offers residents and visitors stunning views of{' '}
          <LinkText href="/rental-listings/city/vancouver/downtown">
            Downtown Vancouver
          </LinkText>{' '}
          and the North Shore Mountains. Known for its blend of residential,
          commercial, and medical facilities, the area provides a balanced urban
          lifestyle. With its rich history, diverse housing options, and
          proximity to key city attractions, Fairview continues to be a
          sought-after community in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,050 - $2,850',
          bed2: '$2,200 - $4,830',
          bed3: '$3,500 - $5,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.fairview-vancouver:Apartment Hunting Fairview Paragraph 1">
            Finding an apartment for rent in Fairview, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Fairview lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.fairview-vancouver:Apartment Hunting Fairview Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Apartments for rent in Fairview Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Fairview, Vancouver
              </LinkText>
              , cater to a diverse range of residents, including young
              professionals and couples. The neighbourhood features a mix of
              modern high-rises and charming low-rise buildings, many offering
              scenic views of the city and mountains. Renting in Fairview
              provides easy access to public transit, shopping districts, and
              recreational facilities, making it a convenient choice for urban
              living.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Houses for rent in Fairview Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Fairview, Vancouver
              </LinkText>
              , are ideal for families and individuals seeking more space and
              privacy. The area boasts a selection of single-family homes and
              duplexes, often situated on tree-lined streets. Residents benefit
              from the neighbourhood’s proximity to reputable schools, parks,
              and medical centres, enhancing the appeal of renting a house in
              this community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Townhouses for rent in Fairview Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Fairview, Vancouver
              </LinkText>{' '}
              , offer a balance between apartment living and standalone homes.
              These multi-level units are perfect for families and professionals
              desiring additional space without compromising on urban amenities.
              Many townhouses in Fairview are located near shopping areas and
              cultural spots, providing a well-rounded living experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Basements for rent in Fairview Paragraph">
              Basements for rent in Fairview, Vancouver, present affordable
              housing options for students and young professionals. These units
              typically offer private entrances and essential amenities. Renting
              a basement suite in Fairview allows residents to enjoy the
              neighbourhood’s vibrant atmosphere and accessibility to public
              transportation.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Condos for rent Fairview Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Fairview, Vancouver
              </LinkText>
              , attract those seeking modern living spaces with amenities like
              gyms and communal areas. The neighbourhood’s condos often feature
              contemporary designs and are situated close to dining, shopping,
              and entertainment venues, making them a popular choice for urban
              dwellers.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Rooms for rent in Fairview Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Fairview, Vancouver
              </LinkText>{' '}
              , are suitable for students and individuals seeking cost-effective
              living arrangements. These accommodations often involve shared
              common areas and provide an opportunity to live in a desirable
              neighbourhood without a hefty price tag. The area’s connectivity
              and amenities add to the convenience of renting a room here.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.fairview-vancouver:Short term rentals in Fairview Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Fairview, Vancouver
              </LinkText>
              , cater to tourists, business travellers, and those in transition.
              Options range from furnished apartments to private homes, offering
              flexibility and comfort. The neighbourhood’s central location
              ensures that short-term renters have quick access to Vancouver’s
              attractions and business districts.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.fairview-vancouver:Exploring Fairview Paragraph">
          Fairview, Vancouver, British Columbia, is a neighborhood that
          seamlessly blends urban living with cultural richness. Known for its
          scenic views of{' '}
          <LinkText href="https://www.tripadvisor.ca/Attraction_Review-g154943-d186164-Reviews-False_Creek-Vancouver_British_Columbia.html">
            False Creek
          </LinkText>{' '}
          and the downtown skyline, Fairview offers a unique mix of residential
          tranquility and commercial vitality. Key attractions include{' '}
          <LinkText href="https://granvilleisland.com/plan-your-visit">
            Granville Island
          </LinkText>
          , a bustling hub of markets, artisan workshops, and theaters, and the
          South Granville Rise, renowned for its upscale shopping and dining
          options. The neighborhood’s tree-lined streets and proximity to
          waterfront parks make it a desirable location for those seeking both
          relaxation and urban amenities.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.fairview-vancouver:Things To Do Fairview Paragraph">
          Fairview offers a diverse range of activities for all interests. Art
          lovers can explore the{' '}
          <LinkText href="https://www.sgga.ca/">
            South Granville Gallery District
          </LinkText>
          , home to contemporary and Indigenous art.{' '}
          <LinkText href="https://granvilleisland.com/">
            Granville Island
          </LinkText>{' '}
          is a must-visit, featuring local artisan markets, street performances,
          and live theater at the Granville Island Stage. Outdoor enthusiasts
          can jog or cycle along the{' '}
          <LinkText href="https://www.tripadvisor.ca/Attraction_Review-g154943-d186164-Reviews-False_Creek-Vancouver_British_Columbia.html">
            False Creek Seawall
          </LinkText>
          , enjoying stunning water and city views. Foodies will appreciate the
          area’s eclectic dining scene, from fine dining establishments to cozy
          coffee shops. With a mix of entertainment, shopping, and outdoor
          recreation, Fairview is a lively neighborhood to explore.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.fairview-vancouver:Restaurants Fairview Paragraph">
        <Paragraph>
          Fairview Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.tripadvisor.ca/Restaurant_Review-g154943-d710048-Reviews-The_Sandbar_Seafood_Restaurant-Vancouver_British_Columbia.html">
            The Sandbar Seafood Restaurant
          </LinkText>{' '}
          - Located on Granville Island, The Sandbar is a must-visit for seafood
          lovers. The restaurant features fresh seafood dishes, including its
          signature cedar-plank salmon. With a waterfront patio offering
          breathtaking views of False Creek, diners enjoy a vibrant atmosphere
          accompanied by live music.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://shizenya.ca/">Shizenya on Broadway</LinkText>{' '}
          - Shizenya offers healthy Japanese cuisine with a focus on organic
          ingredients. Known for its brown rice sushi and fresh seafood options,
          this spot is a favorite for those seeking nutritious yet delicious
          meals.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="https://www.vijs.ca/">Vij’s</LinkText> - Vij’s is
          one of Vancouver’s most renowned Indian restaurants, offering
          innovative and flavorful dishes. Known for its elegant yet welcoming
          ambiance, the restaurant is famous for its lamb popsicles and unique
          spice-infused creations.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.fairview-vancouver:Transportation Fairview Paragraph">
          Getting around Fairview, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Fairview are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Fairview offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.fairview-vancouver:Where To Stay Fairview Paragraph">
          Fairview offers a wide range of accommodations catering to luxury
          travelers, budget-conscious visitors, and short-term guests. For those
          seeking upscale comfort, the{' '}
          <LinkText href="https://granvilleislandhotel.com/">
            Granville Island Hotel
          </LinkText>{' '}
          provides a boutique waterfront experience complete with elegant rooms,
          fine dining, and breathtaking views of False Creek—ideal for couples
          or business travelers. Another luxury choice is the{' '}
          <LinkText href="https://opushotel.com/">Opus Vancouver</LinkText>,
          located just a short distance away in Yaletown, featuring designer
          suites and top-tier service. Travelers looking for affordable yet
          comfortable options can consider{' '}
          <LinkText href="https://www.grandparkdowntownvancouver.com/">
            Grand Park Hotel & Suites
          </LinkText>
          , which offers modern amenities, spacious rooms, and great value. For
          those planning an extended visit or seeking a more home-like
          experience, short-term rentals on{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText> are widely
          available in Fairview. Options range from private rooms and studio
          apartments to entire condos with views of the city or waterfront.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Fairview
